import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/** Fades + slides a section into view the first time it crosses the viewport (IntersectionObserver, no extra deps). */
export function Reveal({ children, delay = 0, y = 28, sx, as: Component = 'div' }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Component
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : `translateY(${y}px)`,
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
        ...sx,
      }}
    >
      {children}
    </Component>
  );
}

export const HOME_COLORS = {
  deep: '#00251a',
  dark: '#004d40',
  mid: '#00796b',
  teal: '#00a389',
  accent: '#26a69a',
  gold: '#ffb300',
  paper: '#f4f9f8',
  sky: '#0ea5e9',
  skyLight: '#7dd3fc',
  skyPale: '#e6f6fd',
};

/** Counts up to `value` once the element enters the viewport; re-animates if `value` changes later (e.g. data arriving after an async fetch). */
export function CountUp({ value, suffix = '', duration = 1400, sx }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const [visible, setVisible] = useState(false);
  const displayRef = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const start = performance.now();
    const from = displayRef.current;
    const delta = value - from;
    let raf;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = Math.round(from + eased * delta);
      displayRef.current = next;
      setDisplay(next);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, value, duration]);

  return <span ref={ref} style={sx}>{display}{suffix}</span>;
}

/** A gentle S-curve wave divider between two sections. */
export function WaveDivider({ fill = '#ffffff', flip = false }) {
  return (
    <Box sx={{ lineHeight: 0, transform: flip ? 'scaleY(-1)' : 'none' }}>
      <svg viewBox="0 0 1440 90" width="100%" height="70" preserveAspectRatio="none" style={{ display: 'block' }}>
        <path
          fill={fill}
          d="M0,32 C240,90 480,0 720,24 C960,48 1200,96 1440,40 L1440,90 L0,90 Z"
        />
      </svg>
    </Box>
  );
}

export const sectionLabelSx = {
  display: 'inline-block',
  px: 2,
  py: 0.5,
  borderRadius: 5,
  bgcolor: 'rgba(0,121,107,0.1)',
  color: HOME_COLORS.dark,
  fontWeight: 700,
  fontSize: '0.75rem',
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  mb: 2,
};
